/**
 * <bubble-filter-overflow> — clamps a wrapping pill row to N rows, keeping the
 * "See more filters" control inline at the end of the last visible row.
 *
 * Why not the theme's existing components:
 *   - <overflow-list> (assets/critical.js) measures with
 *     `top <= rootRect.top`, so it is strictly SINGLE row.
 *   - <show-more-component> (assets/show-more.js) takes a server-rendered count
 *     of items to hide. Pills are variable-width, so no fixed count lands on a
 *     given row count across viewports — measured live, 22 of 36 Fruits pills
 *     wrapped to 3 rows at 1440px and 1120px, and 4 rows at 900px.
 *
 * So this measures instead: show everything, find the row boundaries, hide what
 * falls past `max-rows`, then keep pulling the last visible pill until the
 * control itself fits on the last allowed row. Re-runs on resize and whenever
 * the list is re-rendered (filter clicks morph the section).
 *
 * Progressive enhancement: with no JS every pill renders and nothing is hidden,
 * which is the pre-existing behaviour — never content hidden with no way to reveal.
 *
 * Attributes:
 *   max-rows  rows to keep when collapsed (default 2)
 *
 * Expected DOM:
 *   <bubble-filter-overflow max-rows="2">
 *     <ul data-pill-list>
 *       <li data-pill>…</li>            (repeated)
 *       <li data-more-item><button data-more-button>…</button></li>
 *     </ul>
 *   </bubble-filter-overflow>
 */
const HIDDEN = 'bubble-filter__item--overflow';

/** px slack when grouping elements into rows; well under the ~39px row pitch. */
const ROW_TOLERANCE = 12;

/** px overhang tolerated before pulling another pill to tidy the right edge. */
const ALIGN_TOLERANCE = 2;

class BubbleFilterOverflow extends HTMLElement {
  #raf = 0;

  connectedCallback() {
    if (this.dataset.expanded == null) this.dataset.expanded = 'false';

    this.#button?.addEventListener('click', this.#onToggle);

    this.#resizeObserver.observe(this);
    // Filter clicks re-render the section; re-measure when the pills change.
    this.#mutationObserver.observe(this, { childList: true, subtree: true });

    this.#schedule();

    // A webfont swap re-widths every pill, so a set that just fitted may not any
    // more. The CSS clamp hides the overflow either way; this re-runs the
    // measurement so the pill set matches the font actually rendering, rather
    // than leaving pills clipped. The ResizeObserver alone will not catch it when
    // the host's own box is unchanged.
    document.fonts?.ready.then(() => this.#schedule()).catch(() => {});
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#button?.removeEventListener('click', this.#onToggle);
    cancelAnimationFrame(this.#raf);
  }

  get #list() {
    return this.querySelector('[data-pill-list]');
  }

  get #pills() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-pill]')]);
  }

  get #moreItem() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-more-item]'));
  }

  get #button() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-more-button]'));
  }

  get #maxRows() {
    return Math.max(1, parseInt(this.getAttribute('max-rows') || '2', 10) || 2);
  }

  get #expanded() {
    return this.dataset.expanded === 'true';
  }

  #resizeObserver = new ResizeObserver(() => this.#schedule());

  #mutationObserver = new MutationObserver(() => {
    // Measured SYNCHRONOUSLY, not via #schedule(). MutationObserver callbacks run
    // as microtasks — before the next paint — so hiding the overflow here means
    // the unclamped list never reaches the screen. Going through rAF is one
    // painted frame too late, and that frame is the flash seen when the section
    // re-renders client-side: pagination and filtering both pushState and swap in
    // fresh section HTML, in which every pill is visible.
    //
    // Only childList/subtree is observed, so our own class writes below cannot
    // retrigger this.
    cancelAnimationFrame(this.#raf);

    // Fresh server HTML never carries data-measured, but if THIS host survived
    // the swap it still does — which would leave the CSS pre-clamp released while
    // the new pills are all visible. Drop it first so the clamp covers us even if
    // the measurement below bails out (hidden on mobile, for instance).
    delete this.dataset.measured;

    this.#measure();
  });

  #schedule() {
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => this.#measure());
  }

  #onToggle = (event) => {
    event.preventDefault();
    this.dataset.expanded = this.#expanded ? 'false' : 'true';
    this.#button?.setAttribute('aria-expanded', String(this.#expanded));
    this.#measure();
  };

  #showAll() {
    for (const pill of this.#pills) pill.classList.remove(HIDDEN);
  }

  /**
   * Group elements into rows by top edge, ascending, returning one representative
   * top per row.
   *
   * Grouped with a tolerance rather than by exact value: the control is a hair
   * taller than the pills, so their top edges differ by a sub-pixel amount even
   * when they sit side by side. Bucketing on the exact top counted one row as
   * two and made the row budget impossible to satisfy — the clamp then ate every
   * pill trying to reach it. The tolerance is far below the row pitch (~39px), so
   * genuinely different rows never merge.
   */
  #rowTops(elements) {
    const tops = elements
      .map((el) => el.getBoundingClientRect().top)
      .sort((a, b) => a - b);

    const rows = [];
    for (const top of tops) {
      if (!rows.length || top - rows[rows.length - 1] > ROW_TOLERANCE) rows.push(top);
    }
    return rows;
  }

  /**
   * Rows currently occupied, counting the control — it shares the row box with
   * the pills, so it belongs in the budget rather than being an afterthought.
   * Re-measured on every call: hiding a pill reflows the rest, so any boundary
   * captured earlier is stale.
   */
  #rowsUsed(pills, moreItem) {
    const live = pills.filter((p) => !p.classList.contains(HIDDEN));
    if (!moreItem.classList.contains(HIDDEN)) live.push(moreItem);
    return this.#rowTops(live).length;
  }

  /**
   * Tidy the right edge of the last row against the rows above it.
   *
   * Fitting the row budget alone leaves a ragged block, because the control is
   * wider than the pill it displaces — the last row can overhang the rows above
   * by most of a pill's width.
   *
   * Pulls a pill only while doing so genuinely reduces the misalignment. Pulling
   * unconditionally backfires: a pill is ~95px, so trading a 23px overhang for a
   * 167px shortfall (measured at 1440px) leaves the block looking worse and
   * needlessly hides a category. So compare the deviation either side of the
   * move and keep the tidier one.
   *
   * Only ever hides more than the row budget did, so that budget still holds.
   */
  #alignLastRow(pills, moreItem) {
    let visible = pills.filter((p) => !p.classList.contains(HIDDEN));
    if (!visible.length || moreItem.classList.contains(HIDDEN)) return visible;

    const rows = this.#rowTops([...visible, moreItem]);
    // A single row has nothing above it to align against.
    if (rows.length < 2) return visible;

    const lastRowTop = rows[rows.length - 1];
    const onLastRow = (el) =>
      Math.abs(el.getBoundingClientRect().top - lastRowTop) <= ROW_TOLERANCE;

    // Furthest right edge among the rows above the last one. Those rows never
    // move as we hide trailing pills, so this reference stays valid.
    const above = visible.filter((p) => !onLastRow(p));
    if (!above.length) return visible;
    const reference = Math.max(...above.map((p) => p.getBoundingClientRect().right));

    const deviation = () => Math.abs(moreItem.getBoundingClientRect().right - reference);

    let guard = visible.length + 1;
    while (guard-- > 0) {
      if (moreItem.getBoundingClientRect().right <= reference + ALIGN_TOLERANCE) break;

      const last = visible[visible.length - 1];
      // Stop before stranding the control alone on a row of its own.
      if (!last || !onLastRow(last)) break;

      const before = deviation();
      last.classList.add(HIDDEN);

      // Undo if hiding it overshoots further than the overhang it removed.
      if (deviation() >= before) {
        last.classList.remove(HIDDEN);
        break;
      }

      visible = pills.filter((p) => !p.classList.contains(HIDDEN));
    }

    return visible;
  }

  #measure() {
    const list = this.#list;
    const moreItem = this.#moreItem;
    if (!list || !moreItem) return;

    // Nothing to measure while the row is display:none (mobile uses the drawer).
    if (this.offsetParent === null) return;

    this.#mutationObserver.disconnect();

    const pills = this.#pills;
    this.#showAll();
    moreItem.classList.remove(HIDDEN);

    if (this.#expanded) {
      // Everything visible; the control stays so the row can be collapsed again.
      this.#finish(pills.length, 0);
      return;
    }

    const maxRows = this.#maxRows;

    // Do every pill already fit, with the control hidden? Then no control at all.
    moreItem.classList.add(HIDDEN);
    if (this.#rowsUsed(pills, moreItem) <= maxRows) {
      this.#finish(pills.length, 0);
      return;
    }
    moreItem.classList.remove(HIDDEN);

    // Coarse pass: drop whole rows beyond the budget in one go, so the fine pass
    // below starts close instead of walking every pill one reflow at a time.
    const rows = this.#rowTops(pills);
    const coarseLimit = rows[maxRows - 1] + ROW_TOLERANCE;
    for (const pill of pills) {
      if (pill.getBoundingClientRect().top > coarseLimit) pill.classList.add(HIDDEN);
    }

    // Fine pass: the control is last in flow, so it now trails the last visible
    // pill — and may have pushed itself onto one row too many. Give pills back
    // until everything, control included, is inside the budget.
    let visible = pills.filter((p) => !p.classList.contains(HIDDEN));
    let guard = pills.length + 1;
    while (visible.length && guard-- > 0 && this.#rowsUsed(pills, moreItem) > maxRows) {
      visible.pop()?.classList.add(HIDDEN);
      visible = pills.filter((p) => !p.classList.contains(HIDDEN));
    }

    visible = this.#alignLastRow(pills, moreItem);

    this.#finish(visible.length, pills.length - visible.length);
  }

  /** Publish counts so the label can read "See more filters (14)". */
  #finish(visibleCount, hiddenCount) {
    this.dataset.visibleCount = String(visibleCount);
    this.dataset.hiddenCount = String(hiddenCount);

    // Releases the CSS pre-clamp that held the collapsed height through first
    // paint. Set only once a real measurement has landed, so the clamp is never
    // dropped before the pills have actually been hidden — that would reintroduce
    // the flash it exists to prevent.
    this.dataset.measured = 'true';

    const target = this.querySelector('[data-more-count]');
    if (target) target.textContent = hiddenCount ? ` (${hiddenCount})` : '';

    this.#mutationObserver.observe(this, { childList: true, subtree: true });
  }
}

if (!customElements.get('bubble-filter-overflow')) {
  customElements.define('bubble-filter-overflow', BubbleFilterOverflow);
}
